import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types pour le texteditor
interface TextEditorState {
  listEntreesInText: { prev: any; cur: any };
  nodesOrder: any;
  showEntreePanel: boolean;
  showCommentPanel: boolean;
  entreeHighlighted: boolean;
  activeEditorRef: any;
  activeThreads: any[];
}

// État initial pour textEditor
const initialTextEditorState: TextEditorState = {
  listEntreesInText: { prev: null, cur: null },
  nodesOrder: null,
  showEntreePanel: false,
  showCommentPanel: false,
  entreeHighlighted: true,
  activeEditorRef: null,
  activeThreads: []
};

// Slice pour textEditor
export const textEditorSlice = createSlice({
  name: 'textEditor',
  initialState: initialTextEditorState,
  reducers: {
    setShowEntreePanel: (state, action: PayloadAction<boolean>) => {
      // Comportement exclusif: un seul panel à la fois
      if (action.payload === true) {
        state.showEntreePanel = true;
        state.showCommentPanel = false;
      } else {
        state.showEntreePanel = false;
      }
    },
    setShowCommentPanel: (state, action: PayloadAction<boolean>) => {
      // Comportement exclusif: un seul panel à la fois
      if (action.payload === true) {
        state.showEntreePanel = false;
        state.showCommentPanel = true;
      } else {
        state.showCommentPanel = false;
      }
    },
    setEntreeHighlighted: (state, action: PayloadAction<boolean>) => {
      state.entreeHighlighted = action.payload;
    },
    setActiveEditorRef: (state, action: PayloadAction<any>) => {
      state.activeEditorRef = action.payload;
    },
    setListEntreesInText: (state, action: PayloadAction<{ prev: any; cur: any }>) => {
      state.listEntreesInText = action.payload;
    },
    setNodesOrder: (state, action: PayloadAction<any>) => {
      state.nodesOrder = action.payload;
    },
    resetListEntreesInText: (state) => {
      state.listEntreesInText = { prev: null, cur: null };
    },
    addActiveThread: (state, action: PayloadAction<any>) => {
      state.activeThreads.push(action.payload);
    },
    removeActiveThread: (state, action: PayloadAction<any>) => {
      state.activeThreads = state.activeThreads.filter(thread => thread.id !== action.payload.id);
    },
    setActiveThreads: (state, action: PayloadAction<any[]>) => {
      state.activeThreads = action.payload;
    }
  }
});

// Actions
export const { 
  setShowEntreePanel, 
  setShowCommentPanel, 
  setEntreeHighlighted, 
  setActiveEditorRef,
  setListEntreesInText,
  setNodesOrder,
  resetListEntreesInText,
  addActiveThread,
  removeActiveThread,
  setActiveThreads
} = textEditorSlice.actions;

// Sélecteurs (compatibles avec les imports du lib texteditor)
export const getShowEntreePanel = (state: any) => state.textEditor.showEntreePanel;
export const getShowCommentPanel = (state: any) => state.textEditor.showCommentPanel;
export const getListEntreesInText = (state: any) => state.textEditor.listEntreesInText;
export const getNodesOrder = (state: any) => state.textEditor.nodesOrder;
export const getActiveEditorRef = (state: any) => state.textEditor.activeEditorRef;
export const getActiveThreads = (state: any) => state.textEditor.activeThreads;

// Alias pour compatibilité avec le lib texteditor
export const showEntreePanel = getShowEntreePanel;
export const showCommentPanel = getShowCommentPanel;
export const isEntreeHighlighted = (state: any) => state.textEditor.entreeHighlighted;

// Exports des reducers
export const textEditorReducer = textEditorSlice.reducer;

export default {
  textEditor: textEditorSlice.reducer,
};
